import React, { Fragment, useState, useRef, useEffect, ReactText } from "react";
import ZSpin from "@molecules/ZSpin";
import { SearchOutlined } from "@ant-design/icons";
import { Cascader } from "antd";
import { CascaderOptionType } from "antd/lib/cascader";
import { MergeTagVariable } from "@/Contracts/Documents";
import ZMergeTagAddModal from "./ZMergeTagAddModal";
import ZTypography from "@atoms/ZTypography";
import "./ZMergeTagCascadeDropdown.less";
import { computeMergeTagOptions } from "@/Utils/MergeTags";
import { useFeatureFlag } from "@/Hooks/useFeature";

export interface Props {
	visible: boolean;
	onSubmit: (key: string) => any;
	onClose: () => any;
	mergeTagCustomVariables: MergeTagVariable[];
	mergeTagSystemVariables: MergeTagVariable[];
	shouldAllowAddVariable: boolean;
	triggeredFromButton?: boolean;
	MergeTagListToSkip?: string[];
	isDocumentMessage?: boolean;
	MergeTagChildrenListToSkip?: string[];
	updateMergeTagVariables?: (
		mergeTagVariables: MergeTagVariable[]
	) => Promise<void>;
}

const ADD_VARIABLE_VALUE = "@ADD_VARIABLE@";

const MergeTagCascadeDropdown = React.memo((props: Props) => {
	const mergeTagVariables = [
		...props.mergeTagSystemVariables,
		...props.mergeTagCustomVariables
	];
	const [addVariableModalVisible, setAddVariableModalVisible] =
		useState<boolean>(false);
	const cascaderRef = useRef<Cascader>(null);
	const cascaderWrapperRef = useRef<HTMLDivElement>(null);

	const hideReceiverMergeTag = useFeatureFlag("receiver_merge_tags");

	const searchFilter = (inputValue: string, path: CascaderOptionType[]) => {
		const isPresentInPath = path.some(
			option =>
				option &&
				option.value &&
				String(option.value)
					.toLowerCase()
					.includes(inputValue.toLowerCase()) &&
				option.value !== ADD_VARIABLE_VALUE
		);

		if (!isPresentInPath) {
			const matchingCustomFieldMergeTags = mergeTagVariables.filter(
				mergeTag =>
					mergeTag.display_name
						.toLowerCase()
						.includes(inputValue.toLowerCase())
			);

			if (!matchingCustomFieldMergeTags.length) {
				return false;
			}

			return path.some(option =>
				matchingCustomFieldMergeTags.some(
					customFieldOption =>
						customFieldOption.variable_name === option.value
				)
			);
		}
		return isPresentInPath;
	};

	const searchRender = (inputValue: string, path: CascaderOptionType[]) => {
		const toOutput = path.map(a => a.label).join(".");
		const nowSplit = toOutput.split(inputValue);

		return nowSplit.map((a, index) => (
			<span key={`merge-tag-search-${index}`} title={toOutput}>
				{a}
				{index !== nowSplit.length - 1 ? (
					<span className="text-selection">{inputValue}</span>
				) : (
					""
				)}
			</span>
		));
	};
	useEffect(() => {
		props.visible && cascaderRef.current && cascaderRef.current.focus();
	}, [cascaderRef && cascaderRef.current, props.visible]);

	const escKeyPressed = ($event: KeyboardEvent) => {
		if (props.visible && $event && $event.key === "Escape") {
			props.onClose();
		}
	};

	const handleMouseClickedOutside = ($event: any) => {
		if (
			props.visible &&
			$event &&
			!cascaderWrapperRef.current?.contains($event.target)
		) {
			props.onClose();
		}
	};

	useEffect(() => {
		if (props.visible) {
			window.addEventListener("keydown", escKeyPressed, true);
			if (!addVariableModalVisible)
				window.addEventListener("click", handleMouseClickedOutside);
		} else {
			window.removeEventListener("keydown", escKeyPressed, true);
			window.removeEventListener("click", handleMouseClickedOutside);
		}
		return () => {
			window.removeEventListener("keydown", escKeyPressed, true);
			window.removeEventListener("click", handleMouseClickedOutside);
		};
	}, [escKeyPressed, props.visible, handleMouseClickedOutside]);

	const onChange = (value: ReactText[]) => {
		if (value.length > 0) {
			if (value[0] === ADD_VARIABLE_VALUE) {
				setAddVariableModalVisible(true);
			} else {
				props.onSubmit(String(value[value.length - 1]));
			}
		} else {
			console.error("Selected value on cascasde dropdown has no values!");
			throw new Error(
				"Selected value on cascasde dropdown has no values!"
			);
		}
	};

	const preventCascaderLoseFocus = (event: React.MouseEvent) =>
		event.stopPropagation();

	return (
		<div
			className="merge_tag-dropdown-container"
			ref={cascaderWrapperRef}
			onClick={preventCascaderLoseFocus}
		>
			{props.visible && (
				<Fragment>
					{!!props.shouldAllowAddVariable &&
						props.updateMergeTagVariables && (
							<ZMergeTagAddModal
								visible={addVariableModalVisible}
								onClose={() =>
									setAddVariableModalVisible(false)
								}
								onSubmit={() =>
									setAddVariableModalVisible(false)
								}
								updateMergeTagVariables={
									props.updateMergeTagVariables
								}
								mergeTagSystemVariables={
									props.mergeTagSystemVariables
								}
								mergeTagCustomVariables={
									props.mergeTagCustomVariables
								}
							/>
						)}
					<ZSpin spinning={mergeTagVariables.length === 0}>
						<Cascader
							options={[
								...computeMergeTagOptions({
									mergeTagVariables,
									MergeTagListToSkip:
										props.MergeTagListToSkip,
									isDocumentMessage: props.isDocumentMessage,
									hideReceiverMergeTag,
									MergeTagChildrenListToSkip:
										props.MergeTagChildrenListToSkip
								}),
								...(!!props.shouldAllowAddVariable
									? [
											{
												value: ADD_VARIABLE_VALUE,
												label: (
													<div
														onClick={event => {
															event.stopPropagation();
															setAddVariableModalVisible(
																true
															);
														}}
													>
														<ZTypography.ZLink className="bottom_action">
															Add Variable
														</ZTypography.ZLink>
													</div>
												)
											}
									  ]
									: [])
							]}
							className="search-input text-gray-10"
							placeholder="Search Variable Name"
							showSearch={{
								filter: searchFilter,
								render: searchRender
							}}
							disabled={addVariableModalVisible}
							displayRender={label => label.join(".")}
							suffixIcon={<SearchOutlined />}
							getPopupContainer={trigger =>
								trigger.parentNode as HTMLElement
							}
							allowClear={false}
							ref={cascaderRef}
							popupVisible={true}
							onChange={onChange}
							value={[]}
							popupClassName={`merge-tags-cascader ${
								props.shouldAllowAddVariable
									? "with-bottom-action"
									: ""
							}`}
						/>
					</ZSpin>
				</Fragment>
			)}
		</div>
	);
});

export default MergeTagCascadeDropdown;
